import {
    Component,
    h,
    Host,
    Listen,
    Prop,
    State,
    Watch,
} from '@stencil/core';
import {
    getApolloThemeFromTheme,
    getLocalStorageLastAction,
    resetIdleTimeout,
    ThemeType,
} from '@uipath/portal-shell-util';

import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';

@Component({
    tag: 'ap-timeout-notice-internal',
    styleUrl: 'ap-timeout-notice-internal.scss',
    shadow: false,
})
export class ApTimeoutNoticeInternal {
    /**
     * Theme of the dialog
     * @memberof ApTimeoutNoticeInternal
     * @default 'autoTheme'
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Language of the dialog
     * @memberof ApTimeoutNoticeInternal
     * @default 'en'
     */
    @Prop() language: string = 'en';

    /**
     * Time (in seconds) to wait for user action before logging out
     * @memberof ApTimeoutNoticeInternal
     * @default 60
     */
    @Prop() waitTime: number = 60;

    @State() countdown: number = this.waitTime;

    private _logoutButtonRef: HTMLPortalButtonElement;
    private _extendSessionButtonRef: HTMLPortalButtonElement;
    private _timer: number | null;
    private _apDialog: HTMLApDialogElement;
    private _lastAction: number;

    @Watch('countdown')
    countdownChanged() {
        if (this.countdown <= 0) {
            Logger.info([ 'session', 'idle timeout', 'dialog' ], 'countdown expired');
            this._logout();
        }
    }

    @Listen('sessionTimedOut', { target: 'document' })
    async sessionTimeout(_event: CustomEvent) {
        if (await this._apDialog?.isOpen()) {
            return;
        }

        Logger.debug([ 'ap-shell', 'session' ], 'timeout dialog');
        this._lastAction = getLocalStorageLastAction() ?? Date.now();
        this._openDialog();
    }

    @Listen('keydown', { target: 'document' })
    async handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Tab' && await this._apDialog?.isOpen()) {
            const activeElement = document.activeElement?.shadowRoot?.activeElement;
            const firstFocusableEl = this._logoutButtonRef?.shadowRoot?.querySelector('button');
            const lastFocusableEl = this._extendSessionButtonRef?.shadowRoot?.querySelector('button');

            if (e.shiftKey && activeElement !== this._extendSessionButtonRef) /* shift + tab */ {
                e.preventDefault();
                lastFocusableEl?.focus();
            } else if (!e.shiftKey && activeElement !== this._logoutButtonRef) {
                e.preventDefault();
                firstFocusableEl?.focus();
            }

        }
    }

    render() {
        return (
            <Host class={getApolloThemeFromTheme(this.theme)}>
                <ap-dialog
                    ref={(elem: HTMLApDialogElement) => this._apDialog = elem}
                    preventClose={true}
                    showCloseButton={false}
                    width={416}
                    theme={this.theme}
                    dialogAriaLabel={t('session-expiring', this.language)}
                >
                    <div class="header">
                        <portal-custom-icon name="warning" size="40px" theme={this.theme} />
                        {t('session-expiring', this.language)}
                    </div>
                    <div class="body">
                        <p>{t('session-expiring-description-inactive', this.language, { n: this._calculateInactiveTime() })}</p>
                        <p>{t('session-expiring-description-logged-out', this.language, { n: this.countdown })}</p>
                    </div>
                    <div class="footer">
                        <portal-button
                            ref={(el: HTMLPortalButtonElement) => this._logoutButtonRef = el}
                            type="secondary"
                            theme={this.theme}
                            label={t('logout', this.language)}
                            onClick={() => this._onLogoutClick()}
                        />
                        <portal-button
                            ref={(el: HTMLPortalButtonElement) => this._extendSessionButtonRef = el}
                            type="primary"
                            theme={this.theme}
                            label={t('extend-session', this.language)}
                            onClick={() => this._onExtendSessionClick()}
                        />
                    </div>
                </ap-dialog>
            </Host>
        );
    }

    private _calculateInactiveTime() {
        const currentTime = Date.now();
        const inactiveTime = currentTime - this._lastAction;
        const inactiveTimeInMinutes = Math.floor(inactiveTime / 1000 / 60);
        return inactiveTimeInMinutes;
    }

    private _logout(emitLogoutEvent = false) {
        window.PortalShell?.Session?.logout(emitLogoutEvent);
    }

    private _onLogoutClick() {
        Logger.info([ 'session', 'idle timeout', 'dialog' ], 'logout clicked');

        // We want to emit the event instead because we do not want to show the session expired page when the sign out button is clicked
        this._logout(true);
    }

    private _onExtendSessionClick() {
        Logger.info([ 'session', 'idle timeout', 'dialog' ], 'extend session clicked');
        resetIdleTimeout();
        this._clearTimer();
        this._apDialog.closeDialog();
    }

    private _clearTimer() {
        if (!this._timer) {
            return;
        }

        clearInterval(this._timer);
        this._timer = null;
    }

    private async _openDialog() {
        this.countdown = this.waitTime;

        this._clearTimer();

        if (this.waitTime > 0) {
            this._timer = window.setInterval(async () => {
                if (this.countdown > 0 && await this._apDialog.isOpen()) {
                    this.countdown -= 1;
                    Logger.debug('idle timeout', `${this.countdown}`);
                } else {
                    this._clearTimer();
                }
            }, 1000);
        }

        await this._apDialog.openDialog();
    }
}
